import React, { useEffect, useState } from "react";
import axios from "axios";
import { Accordion, Group, Progress, Stack, Text, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

function StenRow({ row, selected, onSelect }) {
  const sten = Number(row.sten);
  const barValue = Number.isFinite(sten) ? Math.min(100, Math.max(0, sten * 10)) : 0;
  const content = (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Text size="sm" style={{ flex: 1 }}>
        {row.label || row.key}
      </Text>
      <Text size="sm" fw={600} w={28} ta="right">
        {Number.isFinite(sten) ? sten : "—"}
      </Text>
      <Progress value={barValue} w={120} size="sm" />
    </Group>
  );

  if (!onSelect) return content;

  return (
    <UnstyledButton
      onClick={() => onSelect(row.concept)}
      w="100%"
      p={4}
      style={{
        borderRadius: 4,
        background: selected ? "var(--mantine-color-gray-light)" : "transparent",
      }}
    >
      {content}
    </UnstyledButton>
  );
}

export default function AssessmentStenResults({
  assessment,
  onReload,
  selectedConcept,
  onSelectConcept,
}) {
  const { t } = useTranslation();
  const [scoring, setScoring] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const activityStatus = Number(assessment?.activity_status);
  const scored = Number.isFinite(activityStatus) && activityStatus >= 3;

  useEffect(() => {
    if (!assessment?.id) return undefined;
    if (!Number.isFinite(activityStatus) || activityStatus < 2) return undefined;

    if (scored) {
      setLoaded(false);
      axios
        .get(`/assessments/${assessment.id}/scoring-result/`)
        .then(({ data }) => setScoring(data.response[0] || null))
        .catch(() => setScoring(null))
        .finally(() => setLoaded(true));
      return undefined;
    }

    setScoring(null);
    setLoaded(false);
    if (typeof onReload !== "function") return undefined;

    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      onReload();
      if (tries >= 2) clearInterval(timer);
    }, 1500);
    return () => clearInterval(timer);
  }, [assessment?.id, activityStatus, assessment?.scored_at]);

  if (!Number.isFinite(activityStatus) || activityStatus < 2) return null;

  const body = (() => {
    if (!scored || !loaded) {
      return (
        <Text size="sm" c="dimmed">
          {t("sten_scoring")}
        </Text>
      );
    }
    if (!scoring) return null;

    const globals = scoring.globals || [];
    const primaries = scoring.primaries || [];

    return (
      <Stack gap="md">
        {globals.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              {t("sten_globals")}
            </Text>
            {globals.map((row) => (
              <StenRow key={row.key || row.concept} row={row} />
            ))}
          </Stack>
        )}
        {primaries.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              {t("sten_primaries")}
            </Text>
            {primaries.map((row) => (
              <StenRow
                key={row.key || row.concept}
                row={row}
                selected={selectedConcept === row.concept}
                onSelect={onSelectConcept}
              />
            ))}
          </Stack>
        )}
      </Stack>
    );
  })();

  if (!body) return null;

  return (
    <Accordion defaultValue="sten">
      <Accordion.Item value="sten">
        <Accordion.Control>
          <Text size="sm" fw={600}>
            {t("sten_results")}
          </Text>
        </Accordion.Control>
        <Accordion.Panel>{body}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
